import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { dashApi, type DeliveryItem } from "@/lib/dashApi";
import { useAuth } from "@/lib/auth";
import { fmtRelative } from "@/lib/format";
import { EmptyState, ErrorState, LoadingState, PageHeader, Panel, Stat } from "@/components/dash/ui";

const DSTATUS: Record<string, { sym: string; cls: string }> = {
  delivered: { sym: "✓", cls: "text-emerald-300 bg-emerald-500/10 ring-emerald-500/30" },
  failed: { sym: "✗", cls: "text-rose-300 bg-rose-500/10 ring-rose-500/30" },
  skipped: { sym: "—", cls: "text-slate-400 bg-slate-500/10 ring-slate-500/20" },
  suppressed: { sym: "⏸", cls: "text-amber-300 bg-amber-500/10 ring-amber-500/30" },
  pending: { sym: "…", cls: "text-sky-300 bg-sky-500/10 ring-sky-500/30" },
};

function DeliveryStatus({ status }: { status: string }) {
  const m = DSTATUS[status] ?? { sym: "•", cls: "text-slate-300 bg-slate-500/10 ring-slate-500/30" };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${m.cls}`}>
      <span aria-hidden>{m.sym}</span>{status}
    </span>
  );
}

function DeliveryTable({ rows, isAdmin }: { rows: DeliveryItem[]; isAdmin: boolean }) {
  const qc = useQueryClient();
  const retry = useMutation({
    mutationFn: (id: number) => dashApi.retryDelivery(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notif-deliveries"] });
      qc.invalidateQueries({ queryKey: ["notif-overview"] });
    },
  });
  if (!rows.length) return <EmptyState compact>No deliveries in this view.</EmptyState>;
  return (
    <table className="w-full text-sm">
      <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
        <th className="pb-2 font-medium">Subject</th><th className="pb-2 font-medium">Target</th>
        <th className="pb-2 font-medium">Channel</th><th className="pb-2 font-medium">Status</th>
        <th className="pb-2 font-medium">Attempts</th><th className="pb-2 font-medium">Last attempt</th>
        <th className="pb-2 font-medium">Detail</th><th className="pb-2" />
      </tr></thead>
      <tbody className="divide-y divide-ink-500/40">
        {rows.map((d) => (
          <tr key={d.id} className="text-slate-300">
            <td className="py-2 max-w-[260px] truncate">{d.subject}</td>
            <td className="py-2 text-[11px] text-slate-500">{d.target}</td>
            <td className="py-2 text-[11px] text-slate-500">{d.channel_type}</td>
            <td className="py-2"><DeliveryStatus status={d.status} /></td>
            <td className="py-2 tabular-nums text-slate-400">{d.attempts}</td>
            <td className="py-2 text-[11px] text-slate-500">{fmtRelative(d.last_attempt_at ?? d.created_at)}</td>
            <td className="py-2 text-[11px] text-slate-500 max-w-[220px] truncate">{d.detail ?? "—"}</td>
            <td className="py-2 text-right">
              {isAdmin && d.status === "failed" && (
                <button type="button" onClick={() => retry.mutate(d.id)} disabled={retry.isPending}
                  className="rounded-md border border-ink-500/60 bg-ink-800 px-2 py-1 text-[11px] text-slate-200 hover:bg-ink-700 disabled:opacity-60">
                  Retry
                </button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PreferencesPanel() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["notif-prefs"], queryFn: dashApi.notificationPreferences });
  const [form, setForm] = useState<{ daily_briefing_enabled: boolean; report_subscriptions: string[]; alert_severity_threshold: string; channel: string } | null>(null);
  const save = useMutation({
    mutationFn: () => dashApi.setNotificationPreferences(form!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notif-prefs"] }),
  });
  // Seed the editable form from the effective (defaults + stored) prefs once loaded.
  useEffect(() => {
    if (q.data && form === null) {
      const eff = q.data.effective;
      setForm({
        daily_briefing_enabled: Boolean(eff.daily_briefing_enabled),
        report_subscriptions: Array.isArray(eff.report_subscriptions) ? (eff.report_subscriptions as string[]) : [],
        alert_severity_threshold: String(eff.alert_severity_threshold ?? "warning"),
        channel: String(eff.channel ?? "log"),
      });
    }
  }, [q.data, form]);

  if (q.isError) return <ErrorState error={q.error} />;
  if (q.isLoading || !q.data || !form) return <LoadingState label="Loading preferences…" />;
  const toggleKind = (k: string) =>
    setForm((f) => f && ({ ...f, report_subscriptions: f.report_subscriptions.includes(k) ? f.report_subscriptions.filter((x) => x !== k) : [...f.report_subscriptions, k] }));

  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-500">
        These are your personal preferences. IRONDash delivers via shared channels, so your choices flag intent and
        seed defaults — actual delivery still depends on a configured channel below.
      </p>
      <label className="flex items-center gap-2 text-sm text-slate-300">
        <input type="checkbox" checked={form.daily_briefing_enabled} onChange={(e) => setForm({ ...form, daily_briefing_enabled: e.target.checked })} />
        Subscribe to the Daily Briefing
      </label>
      <div>
        <p className="mb-1 text-[11px] uppercase tracking-wide text-slate-500">Report subscriptions</p>
        <div className="flex flex-wrap gap-2">
          {q.data.report_kinds.map((k) => (
            <button key={k} type="button" onClick={() => toggleKind(k)}
              className={`rounded-md px-2.5 py-1 text-xs ${form.report_subscriptions.includes(k) ? "bg-brand-500/20 text-brand-200 ring-1 ring-brand-500/40" : "bg-ink-800 text-slate-400 hover:bg-ink-700"}`}>
              {k}
            </button>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap gap-4">
        <label className="text-sm text-slate-300">
          Alert severity threshold
          <select value={form.alert_severity_threshold} onChange={(e) => setForm({ ...form, alert_severity_threshold: e.target.value })}
            className="ml-2 rounded-md border border-ink-500/60 bg-ink-800 px-2 py-1 text-xs text-slate-200 [color-scheme:dark]">
            {q.data.severities.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label className="text-sm text-slate-300">
          Preferred channel
          <select value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })}
            className="ml-2 rounded-md border border-ink-500/60 bg-ink-800 px-2 py-1 text-xs text-slate-200 [color-scheme:dark]">
            {q.data.channels.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
      </div>
      <button type="button" onClick={() => save.mutate()} disabled={save.isPending}
        className="rounded-lg border border-brand-500/40 bg-brand-500/10 px-3 py-1.5 text-xs text-brand-200 hover:bg-brand-500/20 disabled:opacity-60">
        {save.isPending ? "Saving…" : save.isSuccess ? "Saved ✓" : "Save preferences"}
      </button>
    </div>
  );
}

export default function NotificationCentrePage() {
  const auth = useAuth();
  const isAdmin = (auth.user?.roles ?? []).includes("admin");
  const [tab, setTab] = useState<"all" | "failed" | "suppressed">("all");
  const overview = useQuery({ queryKey: ["notif-overview"], queryFn: dashApi.notificationsOverview });
  const channels = useQuery({ queryKey: ["notif-channels"], queryFn: dashApi.notificationChannels });
  const deliveries = useQuery({
    queryKey: ["notif-deliveries", tab],
    queryFn: () => tab === "failed" ? dashApi.notificationFailures(100)
      : tab === "suppressed" ? dashApi.notificationSuppressed(100)
      : dashApi.notificationDeliveries(100),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Notification Centre"
        description="Delivery reliability for reports, alerts and escalations. Every attempt is logged with its status, retry count and (masked) reason. No external notification is sent unless a channel is explicitly configured — secrets are never shown."
      />
      {overview.data && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <Stat label="Delivered" value={overview.data.delivered} />
          <Stat label="Failed" value={overview.data.failed} />
          <Stat label="Skipped" value={overview.data.skipped} />
          <Stat label="Suppressed" value={overview.data.suppressed} />
          <Stat label="Channels" value={overview.data.channels} />
          <Stat label="Subscriptions" value={overview.data.subscriptions} />
        </div>
      )}

      <Panel title="Channels" subtitle="Configured delivery channels. Webhook URLs and email recipients are masked.">
        {channels.isLoading ? <LoadingState /> : channels.isError ? <ErrorState error={channels.error} /> : !channels.data?.channels.length ? (
          <EmptyState compact>No channels configured.</EmptyState>
        ) : (
          <ul className="space-y-1.5">
            {channels.data.channels.map((c) => (
              <li key={c.id} className="flex items-center gap-3 text-sm">
                <span className={`h-1.5 w-1.5 rounded-full ${c.enabled ? "bg-emerald-400" : "bg-slate-600"}`} aria-hidden />
                <span className="text-slate-200">{c.name}</span>
                <span className="text-[11px] text-slate-500">{c.type}</span>
                {c.config_summary && Object.keys(c.config_summary).length > 0 && (
                  <span className="text-[11px] text-slate-600">{JSON.stringify(c.config_summary)}</span>
                )}
                {!c.enabled && <span className="text-[11px] text-slate-500">disabled</span>}
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel
        title="Delivery history"
        right={
          <div className="flex gap-1.5">
            {(["all", "failed", "suppressed"] as const).map((t) => (
              <button key={t} type="button" onClick={() => setTab(t)}
                className={`rounded-md px-2.5 py-1 text-xs ${tab === t ? "bg-brand-500/20 text-brand-200 ring-1 ring-brand-500/40" : "bg-ink-800 text-slate-400 hover:bg-ink-700"}`}>
                {t}
              </button>
            ))}
          </div>
        }
      >
        {deliveries.isLoading ? <LoadingState /> : deliveries.isError ? <ErrorState error={deliveries.error} /> : (
          <DeliveryTable rows={deliveries.data?.deliveries ?? []} isAdmin={isAdmin} />
        )}
      </Panel>

      <Panel title="My notification preferences">
        <PreferencesPanel />
      </Panel>
    </div>
  );
}
